/**
 * Sportconnect — Search drawer Web Component
 * Snazzy Digital Agency
 *
 * Tiroir recherche AJAX :
 * - Ouverture sur event "sc:search:open" (déclenché par le header)
 * - Predictive search Shopify /search/suggest.json (300ms debounce)
 * - Min 2 caractères, max 6 produits par défaut
 */

class SCSearchDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-search-drawer]');
    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('[data-search-form]');
    this.clearBtn = this.querySelector('[data-search-clear]');
    this.resultsContainer = this.querySelector('[data-search-results]');
    this.emptyState = this.querySelector('[data-search-empty]');
    this.seeAllBox = this.querySelector('[data-search-see-all]');
    this.seeAllLink = this.querySelector('[data-search-see-all-link]');
    this.debounceTimer = null;
    this.lastQuery = '';

    document.addEventListener('sc:search:open', () => this.open());

    this.querySelectorAll('[data-search-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    this.input?.addEventListener('input', (e) => this.onInput(e.target.value));
    this.clearBtn?.addEventListener('click', () => this.clearInput());
  }

  open() {
    if (!this.dialog) return;
    this.dialog.showModal();
    setTimeout(() => this.input?.focus(), 200);
    this.attachSwipeToClose();
  }

  attachSwipeToClose() {
    if (this._swipeAttached) return;
    const dialog = this.dialog;
    if (!dialog) return;
    const handle = dialog.querySelector('[data-drag-handle]');
    if (!handle) return;
    const isMobile = () => window.matchMedia('(max-width: 767.98px)').matches;
    let startY = 0, currentY = 0, dragging = false;
    const onStart = (e) => {
      if (!isMobile()) return;
      const touch = e.touches ? e.touches[0] : e;
      startY = touch.clientY; currentY = 0; dragging = true;
      dialog.style.transition = 'none';
    };
    const onMove = (e) => {
      if (!dragging) return;
      const touch = e.touches ? e.touches[0] : e;
      currentY = Math.max(0, touch.clientY - startY);
      dialog.style.transform = 'translateY(' + currentY + 'px)';
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      dialog.style.transition = '';
      dialog.style.transform = '';
      if (currentY > 60) this.close();
    };
    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: true });
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('touchcancel', onEnd);
    this._swipeAttached = true;
  }

  close() {
    if (!this.dialog) return;
    this.dialog.dataset.closing = 'true';
    setTimeout(() => {
      if (this.dialog) {
        this.dialog.close();
        delete this.dialog.dataset.closing;
      }
    }, 450);
  }

  clearInput() {
    if (!this.input) return;
    this.input.value = '';
    this.clearBtn.dataset.visible = 'false';
    this.renderEmpty();
    this.input.focus();
  }

  onInput(value) {
    const q = (value || '').trim();
    if (this.clearBtn) this.clearBtn.dataset.visible = q.length > 0 ? 'true' : 'false';

    if (q.length < 2) {
      this.renderEmpty();
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(q), 300);
  }

  async search(q) {
    if (q === this.lastQuery) return;
    this.lastQuery = q;
    try {
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('search failed: ' + res.status);
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      this.renderResults(q, products);
    } catch (err) {
      console.error('Search failed', err);
      this.renderEmpty();
    }
  }

  renderEmpty() {
    if (!this.resultsContainer || !this.emptyState) return;
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.appendChild(this.emptyState);
    if (this.seeAllBox) this.seeAllBox.dataset.visible = 'false';
  }

  renderResults(q, products) {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = '';

    if (products.length === 0) {
      const p = document.createElement('div');
      p.className = 'sd__placeholder';
      p.innerHTML = `<p>Aucun résultat pour « ${this.escapeHtml(q)} »</p><small>Essayez avec un autre mot-clé.</small>`;
      this.resultsContainer.appendChild(p);
    } else {
      const title = document.createElement('p');
      title.className = 'sd__group-title';
      title.textContent = 'Produits';
      this.resultsContainer.appendChild(title);

      products.forEach((prod) => {
        const a = document.createElement('a');
        a.className = 'sd__item';
        a.href = prod.url;

        const thumb = document.createElement('span');
        thumb.className = 'sd__item-thumb';
        if (prod.featured_image && prod.featured_image.url) {
          const img = document.createElement('img');
          img.src = prod.featured_image.url;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 56; img.height = 56;
          thumb.appendChild(img);
        }
        a.appendChild(thumb);

        const info = document.createElement('span');
        info.className = 'sd__item-info';
        const t = document.createElement('span');
        t.className = 'sd__item-title';
        t.textContent = prod.title;
        info.appendChild(t);
        if (prod.vendor) {
          const v = document.createElement('span');
          v.className = 'sd__item-vendor';
          v.textContent = prod.vendor;
          info.appendChild(v);
        }
        a.appendChild(info);

        if (prod.price) {
          const price = document.createElement('span');
          price.className = 'sd__item-price';
          price.textContent = prod.price;
          a.appendChild(price);
        }

        this.resultsContainer.appendChild(a);
      });
    }

    if (this.seeAllBox && this.seeAllLink) {
      this.seeAllLink.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(q)}`;
      this.seeAllLink.textContent = `Voir tous les résultats pour « ${q} » →`;
      this.seeAllBox.dataset.visible = 'true';
    }
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('sc-search-drawer')) {
  customElements.define('sc-search-drawer', SCSearchDrawer);
}
